import React, { FunctionComponent, useEffect, useState } from "react";
import {
  Box,
  IconButton,
  ListItemButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  useTheme
} from "@mui/material";
import styles from "./styles.module.scss";
import { strings } from "../../../../localization/LocalizedStrings";
import { useContextMenu } from "../../../../hooks/contextMenu";
import useLongPress from "../../../../hooks/longPress";
import { Icon, IconType } from "../../../../icons";
import { AlbumIdentifierType, IAlbumIdentifier } from "../../../../hooks/albumIdentifiers";
import { ImageCache } from "../../../../models/ImageCache";
import { StatusCodesHelper } from "../../../../models/StatusCodesHelper";
import { useGetImage } from "../../../../hooks/images";
import { LazyImageAspectFit } from "../../../LazyImageAspectFit";
import { ToolbarBox } from "../../../toolbar/ToolbarBox";
import copy from "copy-to-clipboard";
import { toast } from "react-toastify";

export const AlbumIdentifierItem: FunctionComponent<IAlbumIdentifierItemProps> = ({
  isPublic,
  albumIdentifier,
  onModify,
  canMoveDown,
  canMoveUp,
  onMoveDown,
  onMoveUp,
  onDelete,
  canModify
}) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const { handleContextMenu, menuProps, handleMenuClose } = useContextMenu();
  const handleLongPress = useLongPress({ onLongPress: handleContextMenu });
  const typeName = (type: AlbumIdentifierType) => {
    switch (type) {
      case "catalogue":
        return strings.album_identifier_catalogue;
      case "matrix":
        return strings.album_identifier_matrix;
      case "barcode":
        return strings.album_identifier_barcode;
    }
    return undefined;
  };
  const { getImage } = useGetImage();
  const [labelImage, setLabelImage] = useState<string>();
  const fetchLabelImage = async () => {
    const label = albumIdentifier.label;
    setLabelImage(label?.thumbnail);
    if (!label?.hasImage) return;
    const response = await getImage({
      name: label?.uuid,
      width: ImageCache.smallLabelImageSize,
      height: ImageCache.smallLabelImageSize
    });
    if (StatusCodesHelper.isSuccessful(response.status)) {
      if (label.uuid != albumIdentifier.label?.uuid) return;
      setLabelImage(response.body);
    }
  };
  useEffect(() => {
    fetchLabelImage().then();
  }, [albumIdentifier.label]);
  return (
    <div
      key={albumIdentifier.uuid}
      className={styles.container}
      onContextMenu={handleContextMenu}
      {...handleLongPress}
    >
      <ListItemButton disableGutters onClick={() => setIsExpanded(state => !state)}>
        <Stack direction={"row"} flexGrow={1} spacing={1} alignItems={"start"}>
          <Stack>
            <p className={styles.type}>
              {typeName(albumIdentifier.type) ?? albumIdentifier.description}
            </p>
            <p
              className={styles.description}
              style={{
                color: theme.palette.secondary.main,
                WebkitLineClamp: isExpanded ? undefined : 1
              }}
            >
              {typeName(albumIdentifier.type) === undefined ? "" : albumIdentifier.description}
            </p>
          </Stack>
          <Box flexGrow={1} />
          <div className={styles.codePartsDiv}>
            {albumIdentifier.codeParts.map((codePart, index) => {
              if (codePart !== "") {
                return (
                  <ToolbarBox
                    key={`${albumIdentifier.uuid}_codePart_${index}`}
                    className={styles.codePart}
                  >
                    <p className={styles.name}>{codePart}</p>
                  </ToolbarBox>
                );
              }
            })}
          </div>
          {albumIdentifier.label &&
            albumIdentifier.label.hasImage &&
            albumIdentifier.type == "catalogue" && (
              <LazyImageAspectFit
                className={styles.labelImage}
                defaultImage={labelImage}
                image={labelImage}
                alt={""}
              />
            )}
        </Stack>
      </ListItemButton>
      <Stack className={styles.hoverButtonsStack} direction={"row"} spacing={1}>
        <Tooltip title={strings.options} arrow disableInteractive>
          <IconButton
            className={styles.hoverButton}
            style={{ backgroundColor: theme.palette.colors?.counterBackground }}
            onClick={event => {
              event.stopPropagation();
              handleContextMenu(event);
            }}
          >
            <Icon className={styles.hoverButtonIcon} type={IconType.MoreVertical} />
          </IconButton>
        </Tooltip>
      </Stack>
      <Menu {...menuProps}>
        {!isPublic && canModify && (
          <MenuItem
            onClick={async () => {
              handleMenuClose();
              onModify();
            }}
            dense
          >
            <ListItemIcon>
              <Icon type={IconType.EditSquare} />
            </ListItemIcon>
            {strings.alert_modify}
          </MenuItem>
        )}
        <MenuItem
          onClick={async () => {
            handleMenuClose();
            copy(albumIdentifier.codeParts.join(" "));
            toast.success(strings.copy_value_success_notification);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.ContentCopy} />
          </ListItemIcon>
          {strings.copy_value}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            setIsExpanded(state => !state);
          }}
          dense
        >
          <ListItemIcon>
            {isExpanded ? <Icon type={IconType.Compress} /> : <Icon type={IconType.Expand} />}
          </ListItemIcon>
          {isExpanded ? strings.shrink : strings.expand}
        </MenuItem>
        {!isPublic && canMoveUp && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              onMoveUp();
            }}
            dense
          >
            <ListItemIcon>
              <Icon type={IconType.KeyboardDoubleArrowUp} />
            </ListItemIcon>
            {strings.move_up}
          </MenuItem>
        )}
        {!isPublic && canMoveDown && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              onMoveDown();
            }}
            dense
          >
            <ListItemIcon>
              <Icon type={IconType.KeyboardDoubleArrowDown} />
            </ListItemIcon>
            {strings.move_down}
          </MenuItem>
        )}
        {!isPublic && canModify && (
          <MenuItem
            key={"Remove"}
            onClick={() => {
              handleMenuClose();
              onDelete();
            }}
            sx={{ color: theme.palette.colors?.remove }}
            dense
          >
            <ListItemIcon sx={{ color: theme.palette.colors?.remove }}>
              <Icon type={IconType.DeleteForever} />
            </ListItemIcon>
            {strings.alert_delete}
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};

export interface IAlbumIdentifierItemProps {
  isPublic: boolean;
  albumIdentifier: IAlbumIdentifier;
  onModify?: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onDelete?: () => void;
  canModify: boolean;
}
